import PropTypes from 'prop-types'
import React from 'react'

import ACol from '../../../external/components/layout/ACol'
import ARow from '../../../external/components/layout/ARow'

/**
 * @file   : Destination.js
 * @author : Amarjeet Lamba (Amarjeet.Lamba@AspiraConnect.com)
 * @date   : 9/26/2018, 1:42:43 PM
 */
import Cont, { ALIGN_RIGHT } from '../../../external/components/layout/Cont'
import Section, {
  SUB_INLINE_TOP,
} from '../../../external/components/layout/Section'
import ABaseLink from '../../../external/components/link/ABaseLink'
import AIcon, { ICONS } from '../../../external/components/media/icon/AIcon'
import TextStyler, {
  DEFAULT_TEXT,
  FONT_SIZE,
  FONT_WEIGHT,
  LINECLAMP_LIMIT,
  ORANGE_TEXT,
} from '../../../external/components/text/TextStyler'
import {
  dangerouslyDecodeHTMLEntity,
  isEmpty,
  isUndefined,
  stripHTML,
} from '../../../external/utilities/GeneralUtils'
import { ROLES } from '../../../external/utilities/accessibility/Definitions'
import { simpleTranslate, translate } from '../../Translation'

const DestinationSummaryCard = ({ intl, destinationProduct }) => {
  const getDestinationProductUI = function () {
    const getFooterContent = function () {
      const { webURL, currencyCode, priceFormatted } = destinationProduct

      const bookNowLink = (
        <TextStyler
          styleObj={{
            ...DEFAULT_TEXT,
            fontSize: FONT_SIZE.X3,
            fontWeight: FONT_WEIGHT.BOLD,
          }}
        >
          <ABaseLink
            to={dangerouslyDecodeHTMLEntity(webURL)}
            newWin
            internal={false}
          >
            {simpleTranslate(intl, 'label_destination_book_now')}
          </ABaseLink>
        </TextStyler>
      )

      const currency = (
        <TextStyler
          styleObj={{
            ...DEFAULT_TEXT,
            fontSize: FONT_SIZE.X1,
            fontWeight: FONT_WEIGHT.BOLD,
          }}
        >
          {translate(intl, 'label_destination_currency', {
            currency: currencyCode,
          })}
        </TextStyler>
      )

      const price = (
        <TextStyler
          styleObj={{
            ...DEFAULT_TEXT,
            fontSize: FONT_SIZE.X4,
            fontWeight: FONT_WEIGHT.BOLD,
          }}
        >
          {dangerouslyDecodeHTMLEntity(priceFormatted)}
        </TextStyler>
      )

      return (
        <ARow>
          <ACol xs={6}>
            {currency}
            <br />
            {price}
          </ACol>
          <ACol xs={6}>
            <Cont align={ALIGN_RIGHT}>
              <Section />
              {bookNowLink}
            </Cont>
          </ACol>
        </ARow>
      )
    }

    const getDestinationRatings = function () {
      const { rating, reviewCount } = destinationProduct
      const stars = []

      for (let i = 0; i < rating; i++) {
        stars.push(<AIcon icon={ICONS.star} size={'03x'} key={`star-${i}`} />)
      }
      for (let i = 0; i < 5 - rating; i++) {
        stars.push(
          <AIcon icon={ICONS.starOutline} size={'03x'} key={`star-o-${i}`} />,
        )
      }

      return (
        <Cont>
          <TextStyler styleObj={{ ...ORANGE_TEXT }}>{stars}</TextStyler>
          <TextStyler
            styleObj={{
              ...ORANGE_TEXT,
              fontSize: FONT_SIZE.X2,
            }}
          >
            {reviewCount === 1
              ? translate(intl, 'label_destination_review', {
                  destinationReviewCount: reviewCount,
                })
              : translate(intl, 'label_destination_reviews', {
                  destinationReviewCount: reviewCount,
                })}
          </TextStyler>
        </Cont>
      )
    }

    const getDestinationDescription = function () {
      const { shortDescription } = destinationProduct

      return (
        <TextStyler
          styleObj={{
            ...DEFAULT_TEXT,
            fontSize: FONT_SIZE.X2,
            lineClamp: LINECLAMP_LIMIT.L3,
          }}
        >
          {stripHTML(dangerouslyDecodeHTMLEntity(shortDescription))}
        </TextStyler>
      )
    }

    const getDestinationHeading = function () {
      const { webURL, title } = destinationProduct

      return (
        <TextStyler
          styleObj={{
            ...DEFAULT_TEXT,
            fontSize: FONT_SIZE.X2,
            fontWeight: FONT_WEIGHT.BOLD,
          }}
        >
          <ABaseLink
            to={dangerouslyDecodeHTMLEntity(webURL)}
            newWin
            internal={false}
          >
            {dangerouslyDecodeHTMLEntity(title)}
          </ABaseLink>
        </TextStyler>
      )
    }

    const getDestinationLocation = function () {
      const { primaryDestinationName } = destinationProduct

      const locationLabel = (
        <TextStyler
          styleObj={{
            ...DEFAULT_TEXT,
            fontSize: FONT_SIZE.X2,
            fontWeight: FONT_WEIGHT.BOLD,
          }}
        >
          {simpleTranslate(
            intl,
            'label_destination_tours_attractions_location',
          )}
        </TextStyler>
      )

      const destinationName = (
        <TextStyler styleObj={{ fontSize: FONT_SIZE.X2 }}>
          {dangerouslyDecodeHTMLEntity(primaryDestinationName)}
        </TextStyler>
      )

      return (
        <Cont>
          {locationLabel}
          {destinationName}
        </Cont>
      )
    }

    const getBodyContent = function () {
      return (
        <Cont>
          {getDestinationHeading()}
          {getDestinationLocation()}
          <Section style={SUB_INLINE_TOP} />
          {getDestinationRatings()}
          <Section style={SUB_INLINE_TOP} />
          {getDestinationDescription()}
        </Cont>
      )
    }

    const getThumbnail = function () {
      const { thumbnailURL, title, webURL } = destinationProduct
      const imgURL = dangerouslyDecodeHTMLEntity(thumbnailURL)
      const imgTitle = dangerouslyDecodeHTMLEntity(title)
      const imgLink = dangerouslyDecodeHTMLEntity(webURL)

      const imageStyle = { backgroundImage: `url(${imgURL})` }

      return (
        <ABaseLink cmpLabel={imgTitle} to={imgLink} newWin internal={false}>
          <div className={'destination-summary-card__image'} style={imageStyle}>
            <Cont
              asSpan
              cmpSRonly
              cmpLabel={imgTitle}
              cmpRole={ROLES.DOC_STRUCTURE.IMG}
            >
              {imgTitle}
            </Cont>
          </div>
        </ABaseLink>
      )
    }

    return (
      <Cont className={'destination-summary-card'}>
        <Cont className={'destination-summary-card__container'}>
          <Cont className={'destination-summary-card__image-wrapper'}>
            {getThumbnail()}
          </Cont>
          <Cont className={'destination-summary-card__detail'}>
            {getBodyContent()}
          </Cont>
        </Cont>
        <Cont className={'destination-summary-card__footer'}>
          {getFooterContent()}
        </Cont>
      </Cont>
    )
  }

  if (isUndefined(destinationProduct) || isEmpty(destinationProduct)) {
    return null
  }

  return <Cont>{getDestinationProductUI()}</Cont>
}

DestinationSummaryCard.propTypes = {
  intl: PropTypes.object.isRequired,
  destinationProduct: PropTypes.object.isRequired,
}

export default DestinationSummaryCard
